import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  List,
  ListItem,
  Tabs,
  Tab,
  Tooltip
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  PictureAsPdf as PdfIcon,
  Article as ArticleIcon,
  BarChart as BarChartIcon,
  Landscape as LandscapeIcon,
  Agriculture as AgricultureIcon,
  DateRange as DateRangeIcon,
  MoreVert as MoreVertIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Logout as LogoutIcon,
  KeyboardArrowRight as ArrowIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  Grass as GrassIcon,
  CalendarMonth as CalendarIcon,
  Place as PlaceIcon,
  FilterAlt as FilterIcon,
  GroupWork as GroupWorkIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const CustomerDashboardPage = () => {
  const { user, logout } = useAuth();
  const [treeRecords, setTreeRecords] = useState([]);
  const [clientData, setClientData] = useState(null);
  const [statistics, setStatistics] = useState({
    totalTrees: 0,
    averageSexRatio: 0,
    lowRatioCount: 0,
    mediumRatioCount: 0,
    highRatioCount: 0,
    lastUpdated: 'Never'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const [performanceRecords, setPerformanceRecords] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [sexRatioData, setSexRatioData] = useState([]);
  const [pieData, setPieData] = useState([]);

  const COLORS = ['#d32f2f', '#ed6c02', '#2e7d32'];
  
  useEffect(() => {
    loadClientData();
  }, [user]);

  const loadClientData = () => {
    setLoading(true);
    setError('');

    try {
      // Load client data from localStorage
      const savedClients = localStorage.getItem('clients');
      if (savedClients) {
        const clients = JSON.parse(savedClients);
        const clientInfo = clients.find(client => client.id === user.clientId);
        if (clientInfo) {
          setClientData(clientInfo);
        }
      }

      // Load tree records from localStorage
      const savedRecords = localStorage.getItem('treeRecords');
      if (savedRecords) {
        const records = JSON.parse(savedRecords);
        // Filter records for this client
        const clientRecords = records.filter(record => record.clientId === user.clientId);
        setTreeRecords(clientRecords);
        
        // Calculate statistics
        calculateStatistics(clientRecords);
      }
      
      // Load performance records
      const savedPerformance = localStorage.getItem('performanceRecords');
      if (savedPerformance) {
        const performanceData = JSON.parse(savedPerformance);
        // Filter for current client
        const clientPerformance = performanceData.filter(record => record.clientId === user.clientId);
        setPerformanceRecords(clientPerformance);
      }
    } catch (error) {
      console.error('Error loading client data:', error);
      setError('Failed to load data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const calculateStatistics = (records) => {
    if (!records || records.length === 0) {
      return;
    }

    let totalMale = 0;
    let totalFemale = 0;
    let lowRatio = 0;
    let mediumRatio = 0;
    let highRatio = 0;
    let latestDate = null;

    records.forEach(record => {
      totalMale += parseInt(record.maleFlowers || 0);
      totalFemale += parseInt(record.femaleFlowers || 0);
      
      const ratio = parseFloat(record.sexRatio);
      if (ratio <= 0.3) lowRatio++;
      else if (ratio <= 0.5) mediumRatio++;
      else highRatio++;

      const recordDate = new Date(record.timestamp);
      if (!latestDate || recordDate > latestDate) {
        latestDate = recordDate;
      }
    });

    const averageSexRatio = totalMale + totalFemale > 0 
      ? (totalFemale / (totalMale + totalFemale)).toFixed(3) 
      : 0;

    setStatistics({
      totalTrees: records.length,
      averageSexRatio,
      lowRatioCount: lowRatio,
      mediumRatioCount: mediumRatio,
      highRatioCount: highRatio,
      lastUpdated: latestDate ? latestDate.toLocaleDateString() : 'Never'
    });
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExportReport = () => {
    alert('Exporting financial report as PDF... This feature would generate a PDF in a real application.');
    // In a real application, this would generate a PDF with all financial data
  };

  const handlePrintReport = () => {
    alert('Printing financial report... This feature would print the report in a real application.');
    // In a real application, this would open the print dialog
  };

  const getSexRatioColor = (ratio) => {
    if (ratio === undefined || ratio === null) return 'default';
    const ratioNum = parseFloat(ratio);
    if (ratioNum <= 0.3) return 'error';
    if (ratioNum <= 0.5) return 'warning';
    return 'success';
  };

  // Helper function to calculate total profit
  const calculateTotalProfit = () => {
    return performanceRecords
      .filter(record => record.profitAmount)
      .reduce((sum, record) => sum + parseFloat(record.profitAmount || 0), 0);
  };

  // Helper function to calculate total expenses
  const calculateTotalExpenses = () => {
    return performanceRecords
      .filter(record => record.expenseAmount)
      .reduce((sum, record) => sum + parseFloat(record.expenseAmount || 0), 0);
  };

  // Helper function to calculate average monthly profit
  const calculateAverageMonthlyProfit = () => {
    const profits = performanceRecords.filter(record => record.profitAmount);
    if (profits.length === 0) return 0;
    
    const totalProfit = profits.reduce((sum, record) => sum + parseFloat(record.profitAmount || 0), 0);
    return totalProfit / profits.length;
  };

  const processSexRatioData = (records) => {
    if (!records || records.length === 0) {
      setSexRatioData([]);
      setPieData([]);
      return;
    }
    
    // Sort records by date
    const sortedRecords = [...records].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    
    // Group by week/month for chart
    const timeGroups = {};
    
    sortedRecords.forEach(record => {
      const date = new Date(record.timestamp);
      const monthYear = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      
      if (!timeGroups[monthYear]) {
        timeGroups[monthYear] = {
          name: monthYear,
          count: 0,
          totalRatio: 0
        };
      }
      
      const group = timeGroups[monthYear];
      group.count++;
      group.totalRatio += parseFloat(record.sexRatio);
    });
    
    // Convert to array and calculate average
    const chartData = Object.values(timeGroups).map(group => ({
      name: group.name,
      ratio: group.totalRatio / group.count
    }));
    
    setSexRatioData(chartData);
    
    // Process pie chart data
    let lowCount = 0;
    let mediumCount = 0;
    let highCount = 0;
    
    sortedRecords.forEach(record => {
      const ratio = parseFloat(record.sexRatio);
      if (ratio < 0.4) lowCount++;
      else if (ratio <= 0.6) mediumCount++;
      else highCount++;
    });
    
    setPieData([
      { name: 'Low Yield', value: lowCount, color: '#d32f2f' },
      { name: 'Medium Yield', value: mediumCount, color: '#ed6c02' },
      { name: 'High Yield', value: highCount, color: '#2e7d32' }
    ]);
  };
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Client Dashboard
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Monitor your plantation performance and generate reports
        </Typography>
      </Box>

      {/* Client Information Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Plantation Information</Typography>
          <IconButton onClick={handleMenuClick}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleExportReport}>
              <ListItemIcon>
                <PdfIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export Financial Report</ListItemText>
            </MenuItem>
            <MenuItem onClick={handlePrintReport}>
              <ListItemIcon>
                <PrintIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Print Dashboard</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
        
        {clientData && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <BusinessIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5">{clientData.name}</Typography>
                  <Typography variant="subtitle1" color="textSecondary">
                    {clientData.companyName}
                  </Typography>
                </Box>
              </Box>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PlaceIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">{clientData.location || 'Location not specified'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">{clientData.contactNumber || 'No contact number'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">{clientData.email || 'No email'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <GroupWorkIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">{clientData.plantationType || 'Plantation type not specified'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <GrassIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">Soil: {clientData.soilProfile || 'Not specified'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">Age: {clientData.plantationAge || 'Not specified'}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            
            <Grid item xs={12} md={5}>
              <Paper elevation={1} sx={{ p: 2, height: '100%', bgcolor: 'background.default' }}>
                <Typography variant="h6" gutterBottom>
                  Plantation Summary
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="textSecondary">Total Trees Scanned:</Typography>
                  <Typography variant="body2" fontWeight="bold">{treeRecords.length}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="textSecondary">Average Sex Ratio:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {treeRecords.length > 0 
                      ? (treeRecords.reduce((sum, record) => sum + parseFloat(record.sexRatio), 0) / treeRecords.length).toFixed(3) 
                      : 'No data'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="textSecondary">Last Record:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {treeRecords.length > 0 
                      ? new Date(treeRecords.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0].timestamp).toLocaleDateString() 
                      : 'No data'}
                  </Typography>
                </Box>
                
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Tooltip title="Print Report">
                    <IconButton size="small">
                      <PrintIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download Data">
                    <IconButton size="small">
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Performance Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AgricultureIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Trees Scanned</Typography>
              </Box>
              <Typography variant="h3" align="center" sx={{ my: 2 }}>
                {statistics.totalTrees}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BarChartIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Avg. Sex Ratio</Typography>
              </Box>
              <Typography 
                variant="h3" 
                align="center" 
                sx={{ my: 2 }} 
                color={`${getSexRatioColor(statistics.averageSexRatio)}.main`}
              >
                {statistics.averageSexRatio}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LandscapeIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Health Status</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '76px' }}>
                <Chip 
                  label={parseFloat(statistics.averageSexRatio) > 0.5 ? "Excellent" : parseFloat(statistics.averageSexRatio) > 0.3 ? "Good" : "Needs Attention"}
                  color={getSexRatioColor(statistics.averageSexRatio)}
                  sx={{ fontSize: '1.2rem', py: 2.5, px: 1.5 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <DateRangeIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Last Updated</Typography>
              </Box>
              <Typography variant="h6" align="center" sx={{ my: 2 }}>
                {statistics.lastUpdated}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Ratio Distribution */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Sex Ratio Distribution</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Low Ratio Trees</Typography>
                <Typography variant="h4" align="center">{statistics.lowRatioCount}</Typography>
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                  Ratio ≤ 0.3
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Medium Ratio Trees</Typography>
                <Typography variant="h4" align="center">{statistics.mediumRatioCount}</Typography>
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                  0.3 {'<'} Ratio ≤ 0.5
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>High Ratio Trees</Typography>
                <Typography variant="h4" align="center">{statistics.highRatioCount}</Typography>
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                  Ratio {'>'} 0.5
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Financial Health Indicators */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Financial Health Indicators</Typography>
        <Typography variant="body2" paragraph color="text.secondary">
          These indicators can be used for loan applications and financial reporting.
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>Productivity Rating</Typography>
                <Typography variant="h5" color={parseFloat(statistics.averageSexRatio) > 0.4 ? "success.main" : "warning.main"}>
                  {parseFloat(statistics.averageSexRatio) > 0.5 ? "A" : 
                   parseFloat(statistics.averageSexRatio) > 0.4 ? "B" : 
                   parseFloat(statistics.averageSexRatio) > 0.3 ? "C" : "D"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Based on average sex ratio and tree health
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>Yield Potential</Typography>
                <Typography variant="h5" color={parseFloat(statistics.averageSexRatio) > 0.4 ? "success.main" : "warning.main"}>
                  {parseFloat(statistics.averageSexRatio) > 0.5 ? "High" : 
                   parseFloat(statistics.averageSexRatio) > 0.4 ? "Above Average" : 
                   parseFloat(statistics.averageSexRatio) > 0.3 ? "Average" : "Below Average"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Estimated based on sex ratio distribution
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>Financing Eligibility</Typography>
                <Chip 
                  label={parseFloat(statistics.averageSexRatio) > 0.4 ? "Eligible" : "Limited Eligibility"} 
                  color={parseFloat(statistics.averageSexRatio) > 0.4 ? "success" : "warning"}
                  sx={{ mt: 1 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Based on plantation health and performance
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="contained" 
            startIcon={<PdfIcon />}
            onClick={handleExportReport}
          >
            Generate Financial Report
          </Button>
        </Box>
      </Paper>

      {/* Recent Tree Records */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Recent Tree Records</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tree ID</TableCell>
                <TableCell>Date Scanned</TableCell>
                <TableCell>Male Flowers</TableCell>
                <TableCell>Female Flowers</TableCell>
                <TableCell>Sex Ratio</TableCell>
                <TableCell>Health Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {treeRecords.length > 0 ? (
                treeRecords.slice(0, 5).map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>{record.treeId}</TableCell>
                    <TableCell>{new Date(record.timestamp).toLocaleDateString()}</TableCell>
                    <TableCell>{record.maleFlowers}</TableCell>
                    <TableCell>{record.femaleFlowers}</TableCell>
                    <TableCell>{record.sexRatio}</TableCell>
                    <TableCell>
                      <Chip 
                        label={parseFloat(record.sexRatio) > 0.5 ? "Healthy" : parseFloat(record.sexRatio) > 0.3 ? "Fair" : "Poor"}
                        color={getSexRatioColor(record.sexRatio)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">No tree records available</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Performance Summary */}
      {performanceRecords && performanceRecords.length > 0 && (
        <>
          {/* Yield Production Summary */}
          <Paper elevation={2} sx={{ p: 3, mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Yield Production Summary
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Crop Type</TableCell>
                    <TableCell align="right">Yield Amount</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {performanceRecords
                    .filter(record => record.yieldType && record.yieldAmount)
                    .slice(0, 5)
                    .map(record => (
                      <TableRow key={record.id}>
                        <TableCell>{record.date}</TableCell>
                        <TableCell>{record.yieldType}</TableCell>
                        <TableCell align="right">{record.yieldAmount}</TableCell>
                        <TableCell>{record.yieldUnit}</TableCell>
                        <TableCell>{record.notes}</TableCell>
                      </TableRow>
                    ))}
                  {performanceRecords.filter(record => record.yieldType && record.yieldAmount).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No yield production records available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          
          {/* Expense Summary */}
          <Paper elevation={2} sx={{ p: 3, mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Recent Expenses
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {performanceRecords
                    .filter(record => record.expenseType && record.expenseAmount)
                    .slice(0, 5)
                    .map(record => (
                      <TableRow key={record.id}>
                        <TableCell>{record.date}</TableCell>
                        <TableCell>{record.expenseType}</TableCell>
                        <TableCell align="right">RM {parseFloat(record.expenseAmount).toFixed(2)}</TableCell>
                        <TableCell>{record.notes}</TableCell>
                      </TableRow>
                    ))}
                  {performanceRecords.filter(record => record.expenseType && record.expenseAmount).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">No expense records available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          
          {/* Monthly Profit Summary */}
          <Paper elevation={2} sx={{ p: 3, mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Monthly Profit Summary
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 2 }}>
              {performanceRecords
                .filter(record => record.profitAmount)
                .slice(0, 3)
                .map(record => {
                  const monthName = new Date(2000, parseInt(record.month) - 1, 1).toLocaleString('default', { month: 'long' });
                  const profit = parseFloat(record.profitAmount);
                  const color = profit > 0 ? 'success.main' : profit < 0 ? 'error.main' : 'text.primary';
                  
                  return (
                    <Grid item xs={12} md={4} key={record.id}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>
                            {monthName} {record.year}
                          </Typography>
                          <Typography variant="h4" color={color}>
                            RM {profit.toFixed(2)}
                          </Typography>
                          {record.notes && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {record.notes}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
            </Grid>
            
            {performanceRecords.filter(record => record.profitAmount).length === 0 && (
              <Alert severity="info">
                No monthly profit records available yet. Your estate administrator will update this information.
              </Alert>
            )}
          </Paper>
          
          {/* Financial Summary Report */}
          <Paper elevation={2} sx={{ p: 3, mt: 4, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Financial Summary Report
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                This financial summary can be shared with banks and financial institutions for loan applications.
              </Alert>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Total Annual Revenue
                      </Typography>
                      <Typography variant="h5" color="primary.main">
                        RM {calculateTotalProfit().toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Total Expenses
                      </Typography>
                      <Typography variant="h5">
                        RM {calculateTotalExpenses().toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Average Monthly Profit
                      </Typography>
                      <Typography variant="h5" color={calculateAverageMonthlyProfit() > 0 ? "success.main" : "error.main"}>
                        RM {calculateAverageMonthlyProfit().toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                startIcon={<PdfIcon />}
                onClick={handleExportReport}
                sx={{ mr: 2 }}
              >
                Export Financial Report
              </Button>
              
              <Button 
                variant="outlined" 
                startIcon={<PrintIcon />}
                onClick={handlePrintReport}
              >
                Print Report
              </Button>
            </Box>
          </Paper>
        </>
      )}

      <Tabs 
        value={tabValue} 
        onChange={handleTabChange} 
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3 }}
      >
        <Tab label="Overview" icon={<BarChartIcon />} iconPosition="start" />
        <Tab label="Tree Records" icon={<GrassIcon />} iconPosition="start" />
        <Tab label="Notes" icon={<FilterIcon />} iconPosition="start" />
      </Tabs>
      
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sex Ratio Trend
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {sexRatioData.length > 0 ? (
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={sexRatioData}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis 
                          domain={[0, 1]} 
                          ticks={[0, 0.2, 0.4, 0.6, 0.8, 1.0]} 
                        />
                        <RechartsTooltip />
                        <Line 
                          type="monotone" 
                          dataKey="ratio" 
                          stroke="#184d18" 
                          activeDot={{ r: 8 }} 
                          name="Average Sex Ratio"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Alert severity="info">No sex ratio data available for your plantation.</Alert>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Tree Records
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {treeRecords.length > 0 ? (
                  <List>
                    {treeRecords
                      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                      .slice(0, 5)
                      .map((record, index) => (
                        <ListItem key={index} divider={index < Math.min(treeRecords.length, 5) - 1}>
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: 'primary.light', width: 30, height: 30 }}>
                              <GrassIcon fontSize="small" />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText 
                            primary={`Tree ID: ${record.treeId}`}
                            secondary={`Date: ${new Date(record.timestamp).toLocaleDateString()} | Sex Ratio: ${parseFloat(record.sexRatio).toFixed(3)}`}
                          />
                          <Chip 
                            label={
                              parseFloat(record.sexRatio) < 0.4 ? 'Low' : 
                              parseFloat(record.sexRatio) <= 0.6 ? 'Medium' : 'High'
                            }
                            size="small"
                            sx={{ 
                              bgcolor: parseFloat(record.sexRatio) < 0.4 ? '#ffebee' : 
                                      parseFloat(record.sexRatio) <= 0.6 ? '#fff3e0' : '#e8f5e9',
                              color: parseFloat(record.sexRatio) < 0.4 ? '#d32f2f' : 
                                     parseFloat(record.sexRatio) <= 0.6 ? '#ed6c02' : '#2e7d32',
                            }}
                          />
                        </ListItem>
                      ))}
                  </List>
                ) : (
                  <Alert severity="info">No tree records found for your plantation.</Alert>
                )}
                
                {treeRecords.length > 5 && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={() => setTabValue(1)}
                    >
                      View All Records
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sex Ratio Distribution
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {pieData.length > 0 && pieData.some(item => item.value > 0) ? (
                  <Box sx={{ height: 250, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 2 }}>
                      {pieData.map((entry, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box 
                            sx={{ 
                              width: 12, 
                              height: 12, 
                              bgcolor: COLORS[index], 
                              mr: 0.5, 
                              borderRadius: '50%' 
                            }} 
                          />
                          <Typography variant="caption">
                            {entry.name}: {entry.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Alert severity="info">No distribution data available.</Alert>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Plantation Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="textSecondary">Plantation Type</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>{clientData.plantationType}</Typography>
                </Box>
                
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="textSecondary">Soil Profile</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>{clientData.soilProfile}</Typography>
                </Box>
                
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="textSecondary">Fertilizer Type</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>{clientData.fertilizerType || 'Not specified'}</Typography>
                </Box>
                
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="textSecondary">Plantation Age</Typography>
                  <Typography variant="body1">{clientData.plantationAge}</Typography>
                </Box>
                
                {clientData.notes && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="textSecondary">Notes</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: 'background.default' }}>
                      <Typography variant="body2">{clientData.notes}</Typography>
                    </Paper>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {tabValue === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                All Tree Records
              </Typography>
              <Chip 
                label={`${treeRecords.length} records`} 
                size="small" 
                color="primary" 
              />
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            {treeRecords.length > 0 ? (
              <List>
                {treeRecords
                  .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                  .map((record, index) => (
                    <ListItem key={index} divider={index < treeRecords.length - 1}>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: 'primary.light', width: 34, height: 34 }}>
                          <GrassIcon fontSize="small" />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText 
                        primary={`Tree ID: ${record.treeId}`}
                        secondary={
                          <>
                            <Typography component="span" variant="body2">
                              Date: {new Date(record.timestamp).toLocaleString()} 
                            </Typography>
                            <br />
                            <Typography component="span" variant="body2">
                              Sex Ratio: {parseFloat(record.sexRatio).toFixed(3)} | 
                              Female/Male: {record.femaleFlowers}/{record.maleFlowers}
                            </Typography>
                            {record.latitude && record.longitude && (
                              <>
                                <br />
                                <Typography component="span" variant="body2">
                                  Location: {record.latitude.toFixed(5)}, {record.longitude.toFixed(5)}
                                </Typography>
                              </>
                            )}
                          </>
                        }
                      />
                      <Chip 
                        label={
                          parseFloat(record.sexRatio) < 0.4 ? 'Low' : 
                          parseFloat(record.sexRatio) <= 0.6 ? 'Medium' : 'High'
                        }
                        size="small"
                        sx={{ 
                          bgcolor: parseFloat(record.sexRatio) < 0.4 ? '#ffebee' : 
                                  parseFloat(record.sexRatio) <= 0.6 ? '#fff3e0' : '#e8f5e9',
                          color: parseFloat(record.sexRatio) < 0.4 ? '#d32f2f' : 
                                parseFloat(record.sexRatio) <= 0.6 ? '#ed6c02' : '#2e7d32',
                        }}
                      />
                    </ListItem>
                  ))}
              </List>
            ) : (
              <Alert severity="info">No tree records found for your plantation.</Alert>
            )}
          </CardContent>
        </Card>
      )}
      
      {tabValue === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Plantation Notes
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {clientData.notes ? (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="body1">{clientData.notes}</Typography>
              </Paper>
            ) : (
              <Alert severity="info">No notes available for this plantation.</Alert>
            )}
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default CustomerDashboardPage; 